import React, { useEffect, useMemo, useState } from 'react';
import { isEqual } from 'lodash';
import { Column as ColumnEntity } from 'new/elements/Table/types';
import styles from 'new/elements/Table/Table.module.scss';
import { FilterList } from '@mui/icons-material';
import { useTableContext } from 'new/elements/Table/useTableContext';
import { FilterValue } from 'new/elements/Table/useFilter';
import { IconButton, Menu, MenuItem, Badge, PopoverOrigin } from '@mui/material';

const BADGE_STYLES = {
  '& .MuiBadge-dot': {
    right: 2,
    top: 2,
    width: 6,
    height: 6,
  },
};

const MENU_ANCHOR_ORIGIN = {
  vertical: 'bottom',
  horizontal: 'left',
} as PopoverOrigin;

const MENU_TRANSFORM_ORIGIN = {
  vertical: 'top',
  horizontal: 'left',
} as PopoverOrigin;

function FilterIconButtonComponent<T>({ column }: { column: ColumnEntity<T> }) {
  const { filters, onFilterChange } = useTableContext<T>();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const filterValue = filters[column.field as string] ?? '';

  const defaultOption = useMemo(() => {
    if (!column.filterOptions) return undefined;
    const options = column.filterOptions;
    return options.find((option) => option.default);
  }, [column.filterOptions]);

  useEffect(() => {
    if (defaultOption && column?.filterable) {
      onFilterChange(column.field as keyof T, defaultOption.value);
    }
  }, [defaultOption, column?.filterable, column.field, onFilterChange]);

  if (!column.filterable || !column.filterOptions) return null;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleFilterSelect = (value: FilterValue) => {
    const filterValue = value === '' ? null : value;
    onFilterChange(column.field as keyof T, filterValue);
    handleClose();
  };

  const open = Boolean(anchorEl);
  const hasActiveFilter = filterValue !== '' && filterValue !== null && filterValue !== undefined;

  const activeFilterLabel = hasActiveFilter
    ? column.filterOptions.find((opt) => opt.value === filterValue)?.label
    : null;

  return (
    <>
      <IconButton
        size="small"
        onClick={handleClick}
        className={styles.filterIconButton}
        aria-label="filter column"
      >
        <Badge variant="dot" color="primary" invisible={!hasActiveFilter} sx={BADGE_STYLES}>
          <FilterList fontSize="small" />
        </Badge>
      </IconButton>

      {hasActiveFilter && activeFilterLabel && (
        <span className={styles.filterBadge} title={`Filtered by: ${activeFilterLabel}`}>
          {activeFilterLabel}
        </span>
      )}

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={MENU_ANCHOR_ORIGIN}
        transformOrigin={MENU_TRANSFORM_ORIGIN}
      >
        <MenuItem
          onClick={() => handleFilterSelect('')}
          selected={filterValue === '' || !filterValue}
        >
          {column.filterPlaceholder || 'All'}
        </MenuItem>
        {column.filterOptions.map((option) => (
          <MenuItem
            key={option.value}
            onClick={() => handleFilterSelect(option.value)}
            selected={filterValue === option.value}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

export const FilterIconButton = React.memo(FilterIconButtonComponent, (prevProps, nextProps) =>
  isEqual(prevProps.column, nextProps.column),
) as typeof FilterIconButtonComponent;
